
// ✅ Utility functions and constants
export function fmtNumber(x, digits = 0) {
  if (x == null || isNaN(x)) return "";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: digits,
  }).format(Number(x));
}

export function parseYYYYMM(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr);
  return s.length >= 7 ? s.substring(0, 7) : s;
}

// --- Build a breadcrumb label (State shown as full name) ---
export function crumbChainLabel(crumbObj) {
  if (!crumbObj) return "";
  
  // For city data from Merged_Last_Month_Data, use the correct field names
  if (crumbObj.City) {
    const parts = [];
    // Use StateName if available, otherwise fall back to State
    const stateName = crumbObj.StateName || crumbObj.State;
    if (stateName) parts.push(fmtStateFull(stateName));
    
    // Add Metro level
    if (crumbObj.Metro) parts.push(crumbObj.Metro);
    
    // Use CountyName if available, otherwise fall back to County
    const countyName = crumbObj.CountyName || crumbObj.County;
    if (countyName) parts.push(countyName);
    
    if (crumbObj.City) parts.push(crumbObj.City);
    return parts.join(" ▸ ");
  }
  
  // Fallback to original logic for other data types
  const order = ["State", "Metro", "County", "City", "Zipcode", "Neighborhood"];
  return order
    .map((k) => {
      const v = crumbObj[k];
      if (!v) return null;
      return k === "State" ? fmtStateFull(v) : v;
    })
    .filter(Boolean)
    .join(" ▸ ");
}

// --- Build a smart breadcrumb label based on area type ---
export function smartCrumbChainLabel(crumbObj, areaType) {
  if (!crumbObj) return "";
  
  // Build path based on area type using currentMetro data
  const parts = [];
  
  // Use StateName if available, otherwise fall back to State
  const stateName = crumbObj.StateName || crumbObj.State;
  if (stateName) parts.push(fmtStateFull(stateName));
  
  // Add Metro level if available
  if (crumbObj.Metro) parts.push(crumbObj.Metro);
  
  // Use CountyName if available, otherwise fall back to County
  const countyName = crumbObj.CountyName || crumbObj.County;
  if (countyName) parts.push(countyName);
  
  // Add the current level
  if (crumbObj.City) parts.push(crumbObj.City);
  if (crumbObj.Zipcode) parts.push(crumbObj.Zipcode);
  if (crumbObj.Neighborhood) parts.push(crumbObj.Neighborhood);
  
  return parts.join(" ▸ ");
}

// --- Path formatting helpers ---
export const PATH_SEP = " - "; // change to "-" if you prefer no spaces

export const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
  MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  DC: "District of Columbia", PR: "Puerto Rico"
};

export function fmtStateFull(s) {
  if (!s) return "";
  const t = String(s).trim();
  // if already the full name, keep it; else expand from abbrev if we can
  return STATE_NAMES[t] || t;
}

// Normalize ZIP to 5-character string with digits only
export function padZip5(z) {
  if (z === null || z === undefined) return "";
  const s = String(z).trim();
  if (!s) return "";
  const onlyDigits = s.replace(/\D+/g, "");
  return onlyDigits.padStart(5, "0").slice(0, 5);
}

/* ====================== SCORING HELPERS ====================== */

// USER-APPROVED WEIGHTS
export const PERF_WEIGHTS = {
  price12: 0.45,
  price3: 0.15,
  rent12: 0.20,
  daysLatest: 0.10, // lower is better → flipped
  inventoryYoY: 0.10,  // lower YoY is better → flipped
};

// Best Potential weights: price 35%, inventory 20%, DOM 20%, rent 25%
export const POTENTIAL_WEIGHTS = {
  priceGap: 0.35,
  invGap: 0.20,
  domGap: 0.20,
  rentGap: 0.25,
};

export const EPS = 1e-6;

export function addMonths(yyyyMm, delta) {
  const [Y, M] = yyyyMm.split("-").map(Number);
  let y = Y, m = M - 1 + delta; // JS month 0..11
  y += Math.floor(m / 12);
  m = ((m % 12) + 12) % 12;
  return `${y}-${String(m + 1).padStart(2, "0")}`;
}

export function monthsRange(endYYYYMM, startBack, endBack) {
  const out = [];
  for (let k = startBack; k <= endBack; k++) out.push(addMonths(endYYYYMM, k));
  return out;
}

export function linearRegressionSlope(xs, ys) {
  const n = xs.length;
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0, den = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    num += dx * (ys[i] - meanY);
    den += dx * dx;
  }
  return den === 0 ? 0 : num / den;
}

export function trendPctFromWindow(values, isLowerBetter) {
  const clean = values.filter((v) => v != null && !Number.isNaN(v));
  if (clean.length < 8) return null; // need enough points to fit slope
  const ys = clean.map((v) => Math.log(Math.max(Number(v), EPS)));
  const xs = Array.from({ length: ys.length }, (_, i) => i + 1); // 1..n
  const slopePerMonth = linearRegressionSlope(xs, ys);
  let annualPct = slopePerMonth * 1200; // ≈ %/year
  if (isLowerBetter) annualPct = -annualPct; // flip so positive = improvement
  return annualPct;
}

export function zScorePerMetro(valueByMetro) {
  const vals = Object.values(valueByMetro).filter((v) => v != null && Number.isFinite(v));
  if (vals.length < 2) {
    // not enough data to z-score → return raw values unchanged
    return Object.fromEntries(Object.entries(valueByMetro).map(([k, v]) => [k, v]));
  }
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  const sd = Math.sqrt(vals.reduce((a, b) => a + (b - mean) ** 2, 0) / (vals.length - 1)) || 1;
  const out = {};
  for (const [k, v] of Object.entries(valueByMetro)) out[k] = v == null ? null : (v - mean) / sd;
  return out;
}

export function pctChange(aNow, aPrev) {
  const x = Number(aNow), y = Number(aPrev);
  if (!Number.isFinite(x) || !Number.isFinite(y) || y === 0) return null;
  return ((x / y) - 1) * 100;
}

export function buildMetroIndex(allDates) {
  const byMetro = {};
  for (const row of allDates) {
    // Use the appropriate field based on the data structure
    const m = row.Zipcode || row.Metro || row.City || row.County || row.State || row.Neighborhood;
    const d = row.Date;
    if (!m || !d) continue;
    if (!byMetro[m]) byMetro[m] = { byDate: {}, datesSorted: [] };
    byMetro[m].byDate[d] = row;
  }
  for (const m of Object.keys(byMetro)) {
    byMetro[m].datesSorted = Object.keys(byMetro[m].byDate).sort(); // YYYY-MM
  }
  return byMetro;
}

export function getLatestDateGlobal(allDates) {
  const ds = allDates.map(d => d.Date).filter(Boolean).sort();
  return ds.length ? ds[ds.length - 1] : "";
}

export function collectWindow(metroIdx, metric, endYYYYMM, startBack, endBack) {
  const months = monthsRange(endYYYYMM, startBack, endBack);
  return months.map((mm) => {
    const row = metroIdx.byDate[mm];
    const v = row ? row[metric] : null;
    return v != null && v !== "" ? Number(v) : null;
  });
}

export function computeGapsForMetro(metroIdx, endYYYYMM) {
  const METRICS = [
    { key: "Price",     lowerBetter: false },
    { key: "Rent",      lowerBetter: false },
    { key: "Days",      lowerBetter: true  }, // DOM
    { key: "Inventory", lowerBetter: true  },
  ];

  const gaps = {};
  for (const m of METRICS) {
    const w0 = collectWindow(metroIdx, m.key, endYYYYMM, -11, 0);
    const w2 = collectWindow(metroIdx, m.key, endYYYYMM, -23, -12);
    const w3 = collectWindow(metroIdx, m.key, endYYYYMM, -35, -24);
    const w4 = collectWindow(metroIdx, m.key, endYYYYMM, -47, -36);

    const t0 = trendPctFromWindow(w0, m.lowerBetter);
    const t2 = trendPctFromWindow(w2, m.lowerBetter);
    const t3 = trendPctFromWindow(w3, m.lowerBetter);
    const t4 = trendPctFromWindow(w4, m.lowerBetter);

    const pastList = [t2, t3, t4].filter((v) => v != null);
    const pastMean = pastList.length ? pastList.reduce((a, b) => a + b, 0) / pastList.length : null;

    const gap = (t0 != null && pastMean != null) ? (t0 - pastMean) : null;

    if (m.key === "Price")      gaps.priceGap = gap;
    if (m.key === "Rent")       gaps.rentGap  = gap;
    if (m.key === "Days")       gaps.domGap   = gap;
    if (m.key === "Inventory")  gaps.invGap   = gap;
  }
  return gaps;
}

export function computePerformanceInputsForMetro(metroIdx, endYYYYMM) {
  const dNow = metroIdx.byDate[endYYYYMM];
  const d12  = metroIdx.byDate[addMonths(endYYYYMM, -12)];
  const d3   = metroIdx.byDate[addMonths(endYYYYMM, -3)];

  const price12 = (dNow && d12) ? pctChange(dNow.Price, d12.Price) : null;
  const price3  = (dNow && d3)  ? pctChange(dNow.Price,  d3.Price) : null;
  const rent12  = (dNow && d12) ? pctChange(dNow.Rent,   d12.Rent) : null;

  const daysLatest = dNow && dNow.Days != null ? -Number(dNow.Days) : null;          // flip
  const inventoryYoY = dNow && dNow.Inventory_YoY != null ? -Number(dNow.Inventory_YoY) : null; // flip (lower YoY is better)

  return { price12, price3, rent12, daysLatest, inventoryYoY };
}

export function combineWithWeights(obj, weights) {
  const present = Object.keys(weights).filter((k) => obj[k] != null && Number.isFinite(obj[k]));
  if (present.length === 0) return null;
  const sumW = present.reduce((a, k) => a + weights[k], 0);
  return present.reduce((s, k) => s + (weights[k] / sumW) * obj[k], 0);
}

export function rankDescending(valuesByMetro) {
  const entries = Object.entries(valuesByMetro)
    .filter(([, v]) => v != null && Number.isFinite(v))
    .sort((a, b) => b[1] - a[1]);
  const rankByMetro = {};
  entries.forEach(([m], idx) => (rankByMetro[m] = idx + 1));
  return rankByMetro;
}

export function isNum(v) { return v != null && Number.isFinite(v); }

export function hasFullPerformanceData(metroIdx, endYYYYMM) {
  const dNow = metroIdx.byDate[endYYYYMM];
  const d12  = metroIdx.byDate[addMonths(endYYYYMM, -12)];
  const d3   = metroIdx.byDate[addMonths(endYYYYMM, -3)];
  return (
    dNow &&
    isNum(dNow.Price) && isNum(dNow.Rent) && isNum(dNow.Days) && isNum(dNow.Inventory) &&
    d12 && isNum(d12.Price) && isNum(d12.Rent) &&
    d3 && isNum(d3.Price)
  );
}

export function hasFullPotentialData(metroIdx, endYYYYMM) {
  const METRICS = [
    { key: "Price",     lowerBetter: false },
    { key: "Rent",      lowerBetter: false },
    { key: "Days",      lowerBetter: true  },
    { key: "Inventory", lowerBetter: true  },
  ];
  for (const m of METRICS) {
    const w0 = collectWindow(metroIdx, m.key, endYYYYMM, -11, 0);
    const w2 = collectWindow(metroIdx, m.key, endYYYYMM, -23, -12);
    const w3 = collectWindow(metroIdx, m.key, endYYYYMM, -35, -24);
    const w4 = collectWindow(metroIdx, m.key, endYYYYMM, -47, -36);

    const t0 = trendPctFromWindow(w0, m.lowerBetter);
    const t2 = trendPctFromWindow(w2, m.lowerBetter);
    const t3 = trendPctFromWindow(w3, m.lowerBetter);
    const t4 = trendPctFromWindow(w4, m.lowerBetter);

    const pastValid = [t2, t3, t4].filter((v) => v != null).length;

    // need last-year trend + at least 2 of the past windows
    if (t0 == null || pastValid < 2) return false;
  }
  return true;
}

export function computeScores(allDates) {
  const byMetro = buildMetroIndex(allDates);
  const latestGlobal = getLatestDateGlobal(allDates);

  /* -------- PERFORMANCE (require ALL inputs) -------- */
  const perfInputsByMetro = {};
  const eligiblePerf = {};

  for (const [metro, idx] of Object.entries(byMetro)) {
    eligiblePerf[metro] = hasFullPerformanceData(idx, latestGlobal);
    if (eligiblePerf[metro]) {
      perfInputsByMetro[metro] = computePerformanceInputsForMetro(idx, latestGlobal);
    } else {
      perfInputsByMetro[metro] = { price12: null, price3: null, rent12: null, daysLatest: null, inventoryYoY: null };
    }
  }

  const price12_z    = zScorePerMetro(Object.fromEntries(Object.entries(perfInputsByMetro).map(([m, v]) => [m, v.price12])));
  const price3_z     = zScorePerMetro(Object.fromEntries(Object.entries(perfInputsByMetro).map(([m, v]) => [m, v.price3])));
  const rent12_z     = zScorePerMetro(Object.fromEntries(Object.entries(perfInputsByMetro).map(([m, v]) => [m, v.rent12])));
  const daysLatest_z = zScorePerMetro(Object.fromEntries(Object.entries(perfInputsByMetro).map(([m, v]) => [m, v.daysLatest])));
  const inventoryYoY_z  = zScorePerMetro(Object.fromEntries(Object.entries(perfInputsByMetro).map(([m, v]) => [m, v.inventoryYoY])));

  const performanceScoreByMetro = {};
  for (const metro of Object.keys(byMetro)) {
    if (!eligiblePerf[metro]) {
      performanceScoreByMetro[metro] = null; // exclude from ranking
      continue;
    }
    const comp = {
      price12:    price12_z[metro] ?? null,
      price3:     price3_z[metro] ?? null,
      rent12:     rent12_z[metro] ?? null,
      daysLatest: daysLatest_z[metro] ?? null,
      inventoryYoY:  inventoryYoY_z[metro] ?? null,
    };
    // Check if price data is available (mandatory requirement)
    const hasPrice = isNum(comp.price12) || isNum(comp.price3);
    
    if (!hasPrice || (!isNum(comp.price12) && !isNum(comp.price3) && !isNum(comp.rent12) && !isNum(comp.daysLatest) && !isNum(comp.inventoryYoY))) {
      performanceScoreByMetro[metro] = null;
    } else {
      performanceScoreByMetro[metro] = combineWithWeights(comp, PERF_WEIGHTS);
    }
  }

  /* -------- POTENTIAL (require ALL FOUR gaps; each needs >=2 past windows) -------- */
  const eligiblePot = {};
  const gapsKeep = { priceGap: {}, rentGap: {}, domGap: {}, invGap: {} };

  for (const [metro, idx] of Object.entries(byMetro)) {
    eligiblePot[metro] = hasFullPotentialData(idx, latestGlobal);
    if (!eligiblePot[metro]) continue;

    const gaps = computeGapsForMetro(idx, latestGlobal);
    if (gaps.priceGap != null) gapsKeep.priceGap[metro] = gaps.priceGap;
    if (gaps.rentGap  != null) gapsKeep.rentGap[metro]  = gaps.rentGap;
    if (gaps.domGap   != null) gapsKeep.domGap[metro]   = gaps.domGap;
    if (gaps.invGap   != null) gapsKeep.invGap[metro]   = gaps.invGap;
  }

  const priceGap_z = zScorePerMetro(gapsKeep.priceGap);
  const rentGap_z  = zScorePerMetro(gapsKeep.rentGap);
  const domGap_z   = zScorePerMetro(gapsKeep.domGap);
  const invGap_z   = zScorePerMetro(gapsKeep.invGap);

  const potentialScoreByMetro = {};
  for (const metro of Object.keys(byMetro)) {
    if (!eligiblePot[metro]) {
      potentialScoreByMetro[metro] = null;
      continue;
    }
    const comp = {
      priceGap: priceGap_z[metro] ?? null,
      invGap:   invGap_z[metro] ?? null,
      domGap:   domGap_z[metro] ?? null,
      rentGap:  rentGap_z[metro] ?? null,
    };
    if (!isNum(comp.priceGap) || !isNum(comp.invGap) || !isNum(comp.domGap) || !isNum(comp.rentGap)) {
      potentialScoreByMetro[metro] = null;
    } else {
      potentialScoreByMetro[metro] = combineWithWeights(comp, POTENTIAL_WEIGHTS);
    }
  }

  // Ranks (only metros with non-null scores included)
  const perfRankByMetro = rankDescending(performanceScoreByMetro);
  const potRankByMetro  = rankDescending(potentialScoreByMetro);

  return {
    latestGlobal,
    performanceScoreByMetro,
    potentialScoreByMetro,
    perfRankByMetro,
    potRankByMetro,
  };
}

// Helper functions for name matching and data retrieval
export function normalize(s) {
  return (s || "").toString().trim().toLowerCase();
}

export function findBestMatch(input, candidates) {
  const q = normalize(input);
  if (!q) return "";
  const exact  = candidates.find((c) => normalize(c) === q);
  if (exact) return exact;
  const starts = candidates.find((c) => normalize(c).startsWith(q));
  if (starts) return starts;
  const incl   = candidates.find((c) => normalize(c).includes(q));
  return incl || "";
}

// Helper: get the latest snapshot for a metro using fuzzy name match
export function getLatestSnapshot(metro, latestByMetro) {
  if (!metro || !latestByMetro) return null;
  if (latestByMetro[metro]) return latestByMetro[metro];
  const keys = Object.keys(latestByMetro || {});
  const best = findBestMatch(metro, keys);
  return best ? latestByMetro[best] : null;
}

export function getNamesForLevel(areaType, allDates, latestByMetro, levelItems, minPopOnly, populationByCity) {
  // All levels: prefer the preloaded items from Merged_No_Data/Standard for faster dropdown loading
  const fallback = Object.keys(latestByMetro || {});
  let list =
    (Array.isArray(levelItems?.[areaType]) && levelItems[areaType].length)
      ? levelItems[areaType]
      : fallback;

  // No additional filtering - data is already filtered in 50K_5y_screened_data
  return (list || []).slice().sort();
}
